import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { fileService, folderService, groupService } from "../services/api";

const SharePermissionsDialog = ({
  open,
  onClose,
  itemType,
  itemId,
  itemName,
}) => {
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [accessLevel, setAccessLevel] = useState("READ");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Determinar qual serviço usar com base no tipo de item
  const service = itemType === "file" ? fileService : folderService;

  // Buscar permissões e grupos quando o diálogo for aberto
  useEffect(() => {
    if (open && itemId) {
      fetchPermissions();
      fetchGroups();
    }
  }, [open, itemId]);

  // Buscar permissões atuais
  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError("");

      let response;
      if (itemType === "file") {
        response = await fileService.getFilePermissions(itemId);
      } else {
        response = await folderService.getFolderPermissions(itemId);
      }

      setPermissions(response.data);
    } catch (err) {
      console.error("Erro ao buscar permissões:", err);
      setError("Não foi possível carregar as permissões");
    } finally {
      setLoading(false);
    }
  };

  // Buscar grupos
  const fetchGroups = async () => {
    try {
      const response = await groupService.getGroups();
      setGroups(response.data);
    } catch (err) {
      console.error("Erro ao buscar grupos:", err);
      setError("Não foi possível carregar os grupos");
    }
  };

  // Adicionar permissão
  const handleAddPermission = async () => {
    if (!selectedGroupId) return;

    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      await service.shareFolder(itemId, {
        groupId: selectedGroupId,
        accessLevel,
      });

      setSuccessMessage("Permissão adicionada com sucesso");
      setSelectedGroupId("");
      setAccessLevel("READ");
      fetchPermissions();
    } catch (err) {
      console.error("Erro ao adicionar permissão:", err);

      // Verificar a mensagem de erro específica para quando a permissão já existe
      if (err.response && err.response.data && err.response.data.error) {
        if (
          err.response.data.error.includes(
            "Já existe uma permissão configurada para este grupo"
          )
        ) {
          setError(
            "Este grupo já possui uma permissão configurada. Por favor, remova a permissão existente antes de adicionar uma nova."
          );
        } else {
          setError(
            err.response.data.error || "Não foi possível adicionar a permissão"
          );
        }
      } else {
        setError("Não foi possível adicionar a permissão");
      }
    } finally {
      setLoading(false);
    }
  };

  // Remover permissão
  const handleRemovePermission = async (groupId) => {
    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      if (itemType === "file") {
        await fileService.removeFilePermission(itemId, groupId);
      } else {
        await folderService.removeFolderPermission(itemId, groupId);
      }

      setSuccessMessage("Permissão removida com sucesso");
      fetchPermissions();
    } catch (err) {
      console.error("Erro ao remover permissão:", err);
      setError("Não foi possível remover a permissão");
    } finally {
      setLoading(false);
    }
  };

  // Obter nome do grupo a partir do ID
  const getGroupName = (groupId) => {
    const group = groups.find((g) => g.id === groupId);
    return group ? group.name : "Grupo desconhecido";
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Compartilhar {itemType === "file" ? "Arquivo" : "Pasta"}: {itemName}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Typography variant="subtitle1" gutterBottom>
          Adicionar Nova Permissão
        </Typography>

        <TextField
          select
          label="Grupo"
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          fullWidth
          margin="normal"
          disabled={loading}
        >
          <MenuItem value="">
            <em>Selecione um grupo</em>
          </MenuItem>
          {groups.map((group) => (
            <MenuItem key={group.id} value={group.id}>
              {group.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Nível de Acesso"
          value={accessLevel}
          onChange={(e) => setAccessLevel(e.target.value)}
          fullWidth
          margin="normal"
          disabled={loading}
        >
          <MenuItem value="READ">Leitura</MenuItem>
          <MenuItem value="WRITE">Leitura e Escrita</MenuItem>
        </TextField>

        <Button
          variant="contained"
          color="primary"
          onClick={handleAddPermission}
          disabled={!selectedGroupId || loading}
          sx={{ mt: 2 }}
        >
          Adicionar Permissão
        </Button>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom>
          Permissões Atuais
        </Typography>

        {loading ? (
          <CircularProgress sx={{ display: "block", mx: "auto", my: 2 }} />
        ) : permissions.length > 0 ? (
          <List>
            {permissions.map((permission) => (
              <ListItem key={permission.id}>
                <ListItemText
                  primary={
                    permission.group
                      ? getGroupName(permission.groupId)
                      : "Desconhecido"
                  }
                  secondary={`Nível de acesso: ${
                    permission.access === "READ"
                      ? "Leitura"
                      : "Leitura e Escrita"
                  }`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemovePermission(permission.groupId)}
                    disabled={loading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary" align="center">
            Nenhuma permissão encontrada
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharePermissionsDialog;
